import { ChangeDetectorRef, Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UiformBaseComponent } from '../uiform-base/uiform-base.component';
import { ServiceLocatorService } from '@ncs/ncs-lib';
import { ActivatedRoute } from '@angular/router';
import { catchError, of, throwError } from 'rxjs';

@Component({
  selector: 'app-jasper',
  templateUrl: './jasper.component.html',
  styleUrls: ['./jasper.component.scss'],
})
export class JasperComponent extends UiformBaseComponent {
  // Holds current form data (for add/edit)
  jasperform: any = {
    data: {
      id: null,
      name: '',
      description: '',
    },
  };

  // Selected file for upload
  selectedFile: File | null = null;

  // For tracking uploaded image/file id
  imageId: any = 0;

  // All jasper reports list
  jasperReports: any = [];

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
    private cdRef: ChangeDetectorRef
  ) {
    super(locator, route);

    // Capture formId from route params if available
    if (route.snapshot.paramMap.get('id')) {
      this.uiformId = Number(route.snapshot.paramMap.get('id'));
      this.jasperform.data['uiformId'] = this.uiformId;
    }
  }

  // Initialize component
  override ngOnInit() {
    super.ngOnInit();
    this.getReports();
  }

  /**
   * Triggered when a file is selected in file input
   */
  onJasperFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      console.log('📂 Selected file:', this.selectedFile.name);
    }
  }

  /**
   * Reset form when modal closes
   */
  override resetForm() {
    this.jasperform.data = {};
    this.selectedFile = null;

    const input = document.getElementById('jasperFileName') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  /**
   * Step 1: Save Jasper metadata (name, description, etc.)
   */
  uploadJasperReport() {
    if (!this.selectedFile) {
      alert('⚠️ Please enter a report name and select a Jasper file');
      return;
    }

    let url = this.urlRewrite(
      this.locator.getEndpoint('endpoint.jasper') + '/save'
    );
    this.jasperform.data.uiformId = this.uiformId;
    this.locator.http.post(
      url,
      this.jasperform.data,
      (res: any, error: boolean) => {
        if (error) {
          alert('Server Error:' + error);
          return;
        }

        this.jasperform.error = !res.success;
        this.jasperform.message = res.message;

        if (res.success) {
          // Save returned id for file upload
          this.jasperform.data.id = res.result.data.id;

          console.log('✅ Metadata saved:', res);

          // Step 2: upload file
          this.uploadImage();
        }
      }
    );
  }

  /**
   * Step 2: Upload the actual Jasper file
   */
  uploadImage() {
    if (!this.imageId) {
      this.imageId = 0;
    }

    let url = this.urlRewrite(
      this.locator.getEndpoint('endpoint.jasper') +
        `/doc/${this.jasperform.data.id}/${this.imageId}`
    );

    const param = { id: this.jasperform.data.id };

    this.locator.http.postMutipart(
      url,
      param,
      this.selectedFile,
      (res: any, error: any) => {
        if (error) {
          console.error('❌ File upload failed:', error);
          return;
        }

        this.jasperform.error = !res.success;
        this.jasperform.message = res.result.message;

        if (res.success) {
          console.log('✅ File uploaded:', res);

          // Step 3: Final save confirmation (if required by backend)
          // this.finalJasperReport();
          this.getReports();
        }
      }
    );
  }

  /**
   * Step 3: Confirm final save (if backend requires another call)
   */
  finalJasperReport() {
    const url = this.locator.getEndpoint('endpoint.jasper') + '/save';

    this.locator.http.post(
      url,
      this.jasperform.data,
      (res: any, error: boolean) => {
        if (error) {
          alert('Server Error:' + error);
          return;
        }

        this.form.error = !res.success;
        this.form.message = res.message;

        if (res.success) {
          this.jasperform.data.id = res.result.data.id;
          this.id = res.result.data.id;

          console.log('✅ Jasper report saved successfully:', res);

          // Reload reports list
          this.getReports();
        }
      }
    );
  }

  /**
   * Download report file in selected format (PDF/Excel/CSV/HTML)
   */
  downloadFile(item: any, type: string) {
    const url = this.urlRewrite(
      this.locator.getEndpoint('endpoint.jasper') + '/download'
    );

    const payload = {
      format: type,
      id: item.id,
      collectionName: this.usecase.collectionName,
    };

    this.locator.http
      .getClient()
      .post(url, payload, { responseType: 'blob' })
      .subscribe({
        next: (data: Blob) => {
          // Check if backend sent JSON instead of file with HTTP 200
          if (data.type == 'application/json') {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const backendError = JSON.parse(reader.result as string);
                alert(
                  `Error: ${
                    backendError.error ||
                    backendError.message ||
                    'Unknown error'
                  }`
                );
              } catch {
                alert(`Error: ${reader.result}`);
              }
            };
            reader.readAsText(data);
            return; // Stop here, don't try to download file
          }

          // It's a file blob, so set file name accordingly and download
          let fileName = '';
          switch (type.toLowerCase()) {
            case 'pdf':
              fileName = 'User_Sample.pdf';
              break;
            case 'html':
              fileName = 'User_Sample.html';
              break;
            case 'excel':
            case 'csv':
              fileName = 'User_Sample.csv';
              break;
            default:
              fileName = 'User_Sample.dat';
          }

          this.downloadDataAsFile(data, fileName, type);
        },

        error: (err: any) => {
          // Handle HTTP errors with JSON error response in Blob
          if (
            err.error instanceof Blob &&
            err.error.type === 'application/json'
          ) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const backendError = JSON.parse(reader.result as string);
                alert(
                  `Error: ${
                    backendError.error ||
                    backendError.message ||
                    'Unknown error'
                  }`
                );
              } catch {
                alert(`Error: ${reader.result}`);
              }
            };
            reader.readAsText(err.error);
          } else {
            // Other errors (network etc.)
            alert(`Error: ${err.message || err.statusText || 'Unknown error'}`);
          }
        },
      });
  }

  /**
   * Creates blob and downloads file
   */
  public downloadDataAsFile(data: Blob, fileName: string, type: string) {
    const blob = new Blob([data], { type: this.getContentType(type) });
    const downloadAncher = document.createElement('a');
    downloadAncher.style.display = 'none';
    const fileURL = URL.createObjectURL(blob);

    downloadAncher.href = fileURL;
    downloadAncher.download = fileName;
    downloadAncher.click();

    // Clean up memory
    URL.revokeObjectURL(fileURL);
  }

  /**
   * Get content type for file based on extension
   */
  private getContentType(type: string): string {
    switch (type.toLowerCase()) {
      case 'pdf':
        return 'application/pdf';
      case 'excel':
      case 'csv':
        return 'text/csv'; // can also use application/vnd.ms-excel
      case 'html':
        return 'text/html';
      default:
        return 'application/octet-stream';
    }
  }

  /**
   * Fetch all jasper reports for this form
   */
  getReports(): void {
    let _self = this;
    let searchData = {
      formId: this.uiformId,
    };

    _self.locator.config
      .getProperty('endpoint.jasper')
      .then((property: string) => {
        let url = _self.urlRewrite(property + '/search/0');
        _self.locator.http.post(
          url,
          searchData,
          function (res: any, error: boolean) {
            if (error) {
              alert('Server Error:' + error);
              return;
            }

            _self.jasperReports = res.result['data'];
          }
        );
      });
  }

  /**
   * Fetch single jasper report by ID (for edit)
   */
  getReport(id: any, callback?: () => void) {
    let _self = this;
    _self.locator.config
      .getProperty('endpoint.jasper')
      .then((property: string) => {
        let url = _self.urlRewrite(property + '/get/' + id);
        _self.locator.http.get(url, function (res: any, error: boolean) {
          if (error) {
            alert('Server Error:' + error);
            return;
          }

          const result = res.result?.data;
          if (!result) {
            console.error('⚠️ No data returned for report');
            return;
          }

          // Prefill form
          _self.jasperform.data = {
            id: result.id,
            name: result.name,
            description: result.description,
          };

          if (callback) callback();
        });
      });
  }

  /**
   * Delete jasper report
   */
  deleteJasper(id: any) {
    let _self = this;

    if (!confirm('Are you sure you want to delete this report?')) return;

    _self.locator.config
      .getProperty('endpoint.jasper')
      .then((property: string) => {
        let url = _self.urlRewrite(property + `/delete/${id}`);

        _self.locator.http.get(url, function (res: any, error: any) {
          if (error) {
            alert('Delete failed: ' + error);
            return;
          }

          _self.jasperform.message = 'Deleted Successfully';
          _self.jasperform.success = true;

          // Refresh reports
          _self.getReports();
        });
      });
  }
}
